import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Mic, MicOff, PhoneOff, Phone } from "lucide-react";
import api from "../lib/api";
import { TAPIN_RADIO_LOGO } from "../lib/radioBrand";

/**
 * RadioCallInGuest — listener-side page at /radio/call/:token.
 *
 * Flow:
 *   1. Look up token → station info.
 *   2. User taps "Join Call" → request mic → open WS as role=guest → send offer → on answer, ICE exchange → now on-air.
 *   3. User taps Mute / Hangup to control.
 */
export default function RadioCallInGuest() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("idle"); // idle | requesting | connecting | live | error | ended
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState(null);
  const [callerId, setCallerId] = useState(null);

  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/radio/callin/${token}`);
        setInfo(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Call-in link not active");
      }
    })();
  }, [token]);

  const join = async () => {
    setError(null); setState("requesting");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      streamRef.current = stream;
    } catch (e) {
      setError("Microphone access denied"); setState("error"); return;
    }

    let iceServers;
    try {
      const { data } = await api.get("/webrtc/ice-servers");
      iceServers = data?.iceServers || [{ urls: "stun:stun.l.google.com:19302" }];
    } catch { iceServers = [{ urls: "stun:stun.l.google.com:19302" }]; }

    const pc = new RTCPeerConnection({ iceServers });
    pcRef.current = pc;
    for (const t of stream.getAudioTracks()) pc.addTrack(t, stream);

    setState("connecting");
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/api/ws/radio/callin/${token}?role=guest`);
    wsRef.current = ws;

    pc.onicecandidate = (ev) => {
      if (ev.candidate && ws.readyState === 1) {
        ws.send(JSON.stringify({ type: "ice", candidate: ev.candidate }));
      }
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "connected") setState("live");
      if (s === "failed" || s === "disconnected") { setState("ended"); teardown(); }
    };

    ws.onopen = async () => {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      ws.send(JSON.stringify({ type: "offer", sdp: pc.localDescription.sdp }));
    };
    ws.onmessage = async (ev) => {
      let data; try { data = JSON.parse(ev.data); } catch { return; }
      if (data.type === "welcome") setCallerId(data.caller_id);
      if (data.type === "answer") {
        try { await pc.setRemoteDescription({ type: "answer", sdp: data.sdp }); } catch {}
      } else if (data.type === "ice" && data.candidate) {
        try { await pc.addIceCandidate(data.candidate); } catch {}
      }
    };
    ws.onclose = () => { setState((s) => (s === "live" ? "ended" : s)); };
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    streamRef.current?.getAudioTracks()?.forEach((t) => { t.enabled = !next; });
  };

  const teardown = () => {
    try { pcRef.current?.close(); } catch {} pcRef.current = null;
    try { wsRef.current?.close(); } catch {} wsRef.current = null;
    try { streamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch {} streamRef.current = null;
  };
  const hangup = () => { teardown(); setState("ended"); };

  useEffect(() => () => teardown(), []);

  return (
    <div data-testid="radio-callin-guest" className="min-h-screen bg-[#030510] text-white flex flex-col items-center justify-center px-6 pt-24 md:pt-28 pb-10">
      <img src={TAPIN_RADIO_LOGO} alt="Tap IN Radio" className="w-24 h-24 object-contain mb-5 drop-shadow-[0_4px_20px_rgba(220,38,38,0.35)]" />
      <div className="text-red-400 uppercase tracking-[0.3em] text-[10px] font-black mb-1">TAP IN RADIO · Call-in</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-1 text-center">
        {info?.station_name || "Connecting…"}
      </h1>
      <div className="text-white/60 text-sm mb-6">Host: {info?.host_display_name || "DJ"}</div>

      {error && <div className="bg-red-500/20 border border-red-500/40 rounded px-4 py-2 text-red-100 text-sm mb-4">{error}</div>}

      {!info && !error && <div className="text-white/50 text-sm">Resolving link…</div>}

      {info && state === "idle" && (
        <button
          data-testid="callin-guest-join"
          onClick={join}
          className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-lg px-8 py-4 rounded-full inline-flex items-center gap-2 shadow-[0_0_30px_rgba(16,185,129,0.35)]"
        >
          <Phone className="w-5 h-5" /> Join the call
        </button>
      )}

      {(state === "requesting" || state === "connecting") && (
        <div className="text-white/70 text-sm">{state === "requesting" ? "Requesting mic…" : "Connecting…"}</div>
      )}

      {state === "live" && (
        <div className="flex flex-col items-center gap-4">
          <div data-testid="callin-guest-onair" className="inline-flex items-center gap-2 bg-red-500/20 text-red-100 border border-red-500/50 px-4 py-2 rounded-full text-sm font-black uppercase tracking-widest">
            <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            ON AIR
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="callin-guest-mute"
              onClick={toggleMute}
              className={`font-bold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 border ${muted ? "bg-white/10 text-white border-white/30" : "bg-white text-black border-white"}`}
            >
              {muted ? <><MicOff className="w-4 h-4" /> Muted</> : <><Mic className="w-4 h-4" /> Mic on</>}
            </button>
            <button
              data-testid="callin-guest-hangup"
              onClick={hangup}
              className="bg-red-500 hover:bg-red-400 text-white font-bold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2"
            >
              <PhoneOff className="w-4 h-4" /> Hang up
            </button>
          </div>
          <div className="text-white/50 text-xs">Caller {callerId?.slice(0, 6) || "…"}</div>
        </div>
      )}

      {state === "ended" && (
        <div className="text-white/60 text-sm">Call ended · thanks for tapping in.</div>
      )}
    </div>
  );
}
